<mat-toolbar class="primary">
  <span class="adx-contact-group-icon"><i class="fa-solid fa-folder-open"></i></span>
  {{'ASTIMAX.CONTACT.WIDGET.NEW_CONTACT_GROUP' | translate }}
  <span class="adx-toolbar-spacer"></span>
  <button mat-icon-button
    [mat-dialog-close]="true"
    aria-label="Close Button"
    matTooltip="{{'ASTIMAX.CONTACT.WIDGET.CLOSE' | translate }}">
    <fa-icon [icon]="'xmark'"></fa-icon>
  </button>
</mat-toolbar>

<mat-dialog-content class="adx-dialog-content-wrapper">
  <!-- Group Name -->
  <mat-form-field appearance="outline" class="adx-full-width">
    <mat-label>{{'ASTIMAX.CONTACT.WIDGET.NAME' | translate }}</mat-label>
    <input #groupNameInput="ngModel"
      matInput
      [(ngModel)]="groupName"
      (input)="validateGroupName()"
      placeholder="{{'ASTIMAX.CONTACT.WIDGET.ENTER_GROUP_NAME' | translate }}"
      aria-label="Enter group name"
      aria-describedby="group-name-error"
      minlength="2"
      maxlength="50"
      required>
      @if (groupNameInput.invalid && (groupNameInput.dirty || groupNameInput.touched) && groupNameError) {
        <mat-error id="group-name-error">{{ groupNameError }}</mat-error>
      }
    </mat-form-field>

    <!-- Address Books Section -->
    <section class="adx-address-books-section">
      <h3>{{'ASTIMAX.CONTACT.WIDGET.ADD_CONTACTS_FROM_SOURCES' | translate }}</h3>
      <p class="adx-section-subtitle">{{'ASTIMAX.CONTACT.WIDGET.CLICK_SOURCE_TO_OPEN_ADDRESS_BOOK' | translate }}</p>

      <div class="adx-address-books-grid">
        <!-- Manual Contacts -->
        <div class="adx-address-book-card adx-manual-contacts"
          (click)="openAddressBook(ContactSource.MANUAL)"
          (keydown.enter)="openAddressBook(ContactSource.MANUAL)"
          (keydown.space)="$event.preventDefault(); openAddressBook(ContactSource.MANUAL)"
          tabindex="0"
          role="button"
          [attr.aria-label]="'Open manual contacts address book'">
          <div class="adx-card-icon">
            <i class="fas fa-phone" aria-hidden="true"></i>
          </div>
          <div class="adx-card-content">
            <h4>{{'ASTIMAX.CONTACT.WIDGET.MANUAL_CONTACTS' | translate }}</h4>
            <p>{{'ASTIMAX.CONTACT.WIDGET.CONTACTS_CREATED_MANUALLY' | translate }}</p>
            <div class="adx-card-stats">
              <span class="adx-total-count">{{ getManualContactsCount() }} {{'ASTIMAX.CONTACT.WIDGET.TOTAL' | translate }}</span>
              @if (getSelectedManualContactsCount() > 0) {
                <span class="adx-selected-count">
                  {{ getSelectedManualContactsCount() }} {{'ASTIMAX.CONTACT.WIDGET.SELECTED' | translate }}
                </span>
              }
            </div>
          </div>
          <div class="adx-card-action">
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
          </div>
        </div>

        <!-- Extension Contacts -->
        <div class="adx-address-book-card adx-extension-contacts"
          (click)="openAddressBook(ContactSource.EXTENSION)"
          (keydown.enter)="openAddressBook(ContactSource.EXTENSION)"
          (keydown.space)="$event.preventDefault(); openAddressBook(ContactSource.EXTENSION)"
          tabindex="0"
          role="button"
          [attr.aria-label]="'Open extension contacts address book'">
          <div class="adx-card-icon">
            <i class="fas fa-phone" aria-hidden="true"></i>
          </div>
          <div class="adx-card-content">
            <h4>{{'ASTIMAX.CONTACT.WIDGET.EXTENSION_CONTACTS' | translate }}</h4>
            <p>{{'ASTIMAX.CONTACT.WIDGET.INTERNAL_PHONE_SYSTEM_CONTACTS' | translate }}</p>
            <div class="adx-card-stats">
              <span class="adx-total-count">{{ getExtensionContactsCount() }} {{'ASTIMAX.CONTACT.WIDGET.TOTAL' | translate }}</span>
              @if (getSelectedExtensionContactsCount() > 0) {
                <span class="adx-selected-count">
                  {{ getSelectedExtensionContactsCount() }} {{'ASTIMAX.CONTACT.WIDGET.SELECTED' | translate }}
                </span>
              }
            </div>
            @if (isLoadingExtensionContacts()) {
              <span class="adx-loading-indicator">
                <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                {{'ASTIMAX.CONTACT.WIDGET.TRYING_TO_LOAD_EXTENSION_CONTACTS' | translate }}...
              </span>
            }
          </div>
          <div class="adx-card-action">
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Selection -->
    <div class="adx-contact-selection" role="region" aria-label="Contact selection">
      <h3 id="contacts-heading">{{'ASTIMAX.CONTACT.WIDGET.SELECT_CONTACTS' | translate }}</h3>

      <!-- Selection Actions - All buttons in one line with consistent styling -->
      <div class="adx-select-actions" role="toolbar" aria-label="Selection actions">
        <div class="adx-button-group">
          <!-- Sorting Buttons -->
          <button
            mat-raised-button
            [class.active]="sortBy() === 'selection'"
            (click)="setSortBy('selection')"
            type="button"
            class="adx-action-btn adx-sort-btn"
            matTooltip="{{'ASTIMAX.CONTACT.WIDGET.SORT_BY_SELECTION' | translate }}">
            <i class="fas fa-hand-pointer" aria-hidden="true"></i>
            {{'ASTIMAX.CONTACT.WIDGET.SELECTED_FIRST' | translate }}
          </button>

          <button
            mat-raised-button
            [class.active]="sortBy() === 'alphabetical'"
            (click)="setSortBy('alphabetical')"
            type="button"
            class="adx-action-btn adx-sort-btn"
            matTooltip="{{'ASTIMAX.CONTACT.WIDGET.SORT_ALPHABETICALLY' | translate }}">
            <i class="fas fa-sort-alpha-down" aria-hidden="true"></i>
            {{'ASTIMAX.CONTACT.WIDGET.ALPHABETICAL' | translate }}
          </button>
        </div>
        <div class="adx-button-group action-buttons">
          @if (selectedContactIds.length > 0) {
            <button
              mat-raised-button
              (click)="clearAllSelections()"
              type="button"
              class="adx-action-btn clear-btn"
              [attr.aria-label]="'Clear all contact selections'"
              matTooltip="{{'ASTIMAX.CONTACT.WIDGET.CANCEL_SELECTION_OF_ALL_CONTACTS' | translate }}">
              <i class="fas fa-times" aria-hidden="true"></i>
              {{'ASTIMAX.CONTACT.WIDGET.CANCEL_SELECTION' | translate }}
            </button>
          }
        </div>
      </div>

      <!-- Contact List - Only Selected Contacts (draggable) -->
      <div class="adx-contact-list"
        role="listbox"
        aria-multiselectable="true"
        aria-labelledby="contacts-heading"
        [attr.aria-activedescendant]="null">

        <!-- Show message when no contacts selected -->
        @if (selectedContactIds.length === 0) {
          <div class="adx-no-results"
            role="status"
            aria-live="polite">
            <i class="fas fa-users" aria-hidden="true"></i>
            <span>{{'ASTIMAX.CONTACT.WIDGET.NO_CONTACTS_SELECTED' | translate }}</span>
            <p class="hint">{{'ASTIMAX.CONTACT.WIDGET.USE_ADDRESS_BOOKS_TO_ADD_CONTACTS' | translate }}</p>
          </div>
        }

        <!-- Selected Contact Items (draggable) -->
        @if (selectedContactIds.length > 0) {
          <div class="adx-selected-contacts-list"
            cdkDropList
            (cdkDropListDropped)="onContactReorder($event)"
            [cdkDropListData]="getSortedSelectedContacts()">

            @for (contactRef of getSortedSelectedContacts(); track contactRef.contactId + contactRef.source; let i = $index) {
              @if (!(contactRef.source === 'extension' && isLoadingExtensionContacts())) {
                <div
                  class="adx-selected-contact-item"
                  cdkDrag
                  [cdkDragData]="contactRef"
                  role="option"
                  [attr.aria-selected]="true"
                  [attr.aria-label]="getSelectedContactAriaLabel(contactRef, i)"
                  tabindex="0">

                  <!-- Drag Handle -->
                  <div class="adx-drag-handle" cdkDragHandle aria-label="Drag to reorder">
                    <i class="fas fa-grip-vertical" aria-hidden="true"></i>
                  </div>

                  <div class="adx-contact-avatar" aria-hidden="true">
                    @if (getContactFromReference(contactRef)?.avatar) {
                      <img [src]="getContactFromReference(contactRef)?.avatar"
                        [alt]="'Profile picture of ' + getContactFromReference(contactRef)?.name"
                        class="adx-avatar-image">
                      } @else {
                        <div class="adx-avatar-initials"
                          [attr.aria-label]="'Initials of ' + getContactFromReference(contactRef)?.name">
                          {{ getContactFromReference(contactRef)?.initials || ContactUtils.getInitials(getContactFromReference(contactRef)?.name || '') }}
                        </div>
                      }
                    </div>

                    <div class="adx-contact-info">
                      <div class="adx-contact-name">{{ getContactFromReference(contactRef)?.name }}</div>
                      <div class="adx-contact-details">
                        <span class="adx-phone-number">[{{ getDisplayPhoneNumberSafe(getContactFromReference(contactRef)) }}]</span>
                        <span class="adx-contact-source"
                          [class]="'source-' + contactRef.source">
                          {{ ContactUtils.getContactSourceLabel(contactRef.source, translate) }}
                        </span>
                      </div>
                    </div>

                    <!-- Position Indicator -->
                    <div class="adx-position-indicator">
                      <span class="position-number">{{ getContactDisplayPosition(contactRef, i) }}</span>
                    </div>

                    <div class="adx-contact-selection-indicator" aria-hidden="true">
                      <button mat-icon-button
                        (click)="removeSelectedContact(contactRef)"
                        type="button"
                        class="adx-remove-contact-btn"
                        [attr.aria-label]="'Remove ' + getContactFromReference(contactRef)?.name + ' from selection'"
                        matTooltip="{{'ASTIMAX.CONTACT.WIDGET.REMOVE_FROM_SELECTION' | translate }}">
                        <i class="fas fa-circle-minus" aria-hidden="true"></i>
                      </button>
                    </div>

                    <!-- Drag Preview -->
                    <div *cdkDragPreview class="adx-drag-preview">
                      <div class="contact-preview">
                        <div class="adx-avatar-initials">
                          {{ getContactFromReference(contactRef)?.initials || ContactUtils.getInitials(getContactFromReference(contactRef)?.name || '') }}
                        </div>
                        <span>{{ getContactFromReference(contactRef)?.name }}</span>
                      </div>
                    </div>
                  </div>
                }
              }
            </div>
          }
        </div>

        <!-- Selection Summary -->
        <div class="adx-selection-summary"
          role="status"
          aria-live="polite"
          aria-label="Selection summary">
          <i class="fas fa-info-circle" aria-hidden="true"></i>
          {{ getSelectedContactsCount() }} {{'ASTIMAX.CONTACT.WIDGET.CONTACTS_SELECTED' | translate }}
          @if (selectedContactIds.length > 1) {
            <span class="adx-reorder-hint">
              • {{'ASTIMAX.CONTACT.WIDGET.DRAG_TO_REORDER' | translate }}
            </span>
          }
        </div>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-raised-button
        type="button"
        (click)="onCancel()"
        aria-label="Cancel group creation"
        matTooltip="{{'ASTIMAX.CONTACT.WIDGET.CANCEL' | translate }}">
        {{'ASTIMAX.CONTACT.WIDGET.CANCEL' | translate }}
      </button>
      <button mat-raised-button
        class="primary"
        type="button"
        (click)="onCreateGroup()"
        [attr.aria-label]="isFormValid() ? 'Create contact group' : 'Create not possible - form invalid'"
        matTooltip="{{'ASTIMAX.CONTACT.WIDGET.SAVE' | translate }}">
        {{'ASTIMAX.CONTACT.WIDGET.SAVE' | translate }}
      </button>
    </mat-dialog-actions>
